import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DotDivider from './DotDivider';
import { BRAND_NAME } from '../constants/brand';

const StyledFooter = styled.footer`
  background-color: rgba(10, 10, 20, 0.85);
  color: ${props => props.theme.colors.textLighter};
  padding: 0 0 ${props => props.theme.spacing.lg} 0;
  text-align: center;
  margin-top: auto;
  font-size: clamp(0.75rem, 1.8vw, 0.85rem);
`;

const FooterBody = styled.div`
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.md} 0;
`;

const CopyrightText = styled.p`
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const FooterLink = styled(Link)`
  color: ${props => props.theme.colors.success};
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const BadgeWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
  margin-top: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const TrustpilotSection = styled.section`
  margin: 0;
  text-align: center;
`;

const TrustpilotLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  background: transparent;
  transition: opacity ${props => props.theme.transitions.fast}, transform ${props => props.theme.transitions.fast};

  &:hover {
    transform: translateY(-2px);
    opacity: 0.8;
  }

  img {
    display: block;
    width: 120px;
    height: auto;
    aspect-ratio: 140 / 34;
  }
`;

const Footer = () => {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();
    const businessUnitId = process.env.REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID;
    const templateId = process.env.REACT_APP_TRUSTPILOT_TEMPLATE_ID;
    const profileUrl = 'https://www.trustpilot.com/review/catsmoker.vercel.app';

    useEffect(() => {
      if (!businessUnitId || !templateId || document.querySelector('script[data-trustpilot-bootstrap]')) {
        return undefined;
      }

      const script = document.createElement('script');
      script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
      script.async = true;
      script.dataset.trustpilotBootstrap = 'true';
      document.body.appendChild(script);

      return () => script.remove();
    }, [businessUnitId, templateId]);

    return (
        <StyledFooter>
            <DotDivider aria-hidden="true" />
            <FooterBody>
            <BadgeWrap>
              <a
                href="https://www.dmca.com/compliance/catsmoker.vercel.app"
                title="DMCA Compliance information for catsmoker.vercel.app"
              >
                <img
                  src="https://www.dmca.com/img/dmca-compliant-grayscale.png"
                        alt="DMCA compliant"
                        width="155"
                        height="45"
                        loading="lazy"
                        decoding="async"
                        style={{ width: '155px', height: 'auto', aspectRatio: '155 / 45', maxWidth: '100%', objectFit: 'contain', display: 'block', filter: 'invert(1)' }}
                />
              </a>
              <TrustpilotSection aria-label="Trustpilot">
                {businessUnitId && templateId ? (
                  <div
                    className="trustpilot-widget"
                    data-locale="en-US"
                    data-template-id={templateId}
                    data-businessunit-id={businessUnitId}
                    data-style-height="52px"
                    data-style-width="100%"
                  >
                    <TrustpilotLink href={profileUrl} target="_blank" rel="noopener noreferrer" aria-label="Trustpilot profile">
                      <img src="/assets/images/trustpilot-logo-white.svg" alt="Trustpilot" width="120" height="29" loading="lazy" decoding="async" />
                    </TrustpilotLink>
                  </div>
                ) : (
                  <TrustpilotLink href={profileUrl} target="_blank" rel="noopener noreferrer" aria-label="Trustpilot profile">
                    <img src="/assets/images/trustpilot-logo-white.svg" alt="Trustpilot" width="120" height="29" loading="lazy" decoding="async" />
                  </TrustpilotLink>
                )}
              </TrustpilotSection>
            </BadgeWrap>
            <CopyrightText>© 2024-{currentYear} {BRAND_NAME}. {t('footer.rights_reserved')}</CopyrightText>
            <FooterLink to="/legal">{t('footer.legal')}</FooterLink>
            </FooterBody>
        </StyledFooter>
    );
};

export default Footer;